import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  TextInput,
} from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { AdminDashboardOrgBrief } from './types';
import { useStoreActions } from '../../reducers';
import { SlamStatusResponse } from '../../reducers/adminDashboard';
import { formatBytesToGB } from '../../utils/misc';

type AdminStorageConfigProps = {
  handleClose: () => void;
} & Pick<
  AdminDashboardOrgBrief,
  'organization_id' | 'allowed_space' | 'upload_limit'
>;

export default function AdminStorageConfig({
  organization_id,
  allowed_space,
  // upload_limit,
  handleClose,
}: AdminStorageConfigProps) {
  const [allowedSpace, setAllowedSpace] = useState(allowed_space);
  // const [uploadLimit, setUploadLimit] = useState(upload_limit);
  const [isUpdating, setIsUpdating] = useState(false);
  const [slamEnabled, setSlamEnabled] = useState(false);
  const [initialSlamEnabled, setInitialSlamEnabled] = useState(false);
  const [isLoadingSlamStatus, setIsLoadingSlamStatus] = useState(true);
  const queryClient = useQueryClient();

  const { updateOrgAllowedSpace, getSlamStatus, setSlamStatus } =
    useStoreActions((actions) => actions.adminDashboard);

  useEffect(() => {
    const fetchSlamStatus = async () => {
      try {
        setIsLoadingSlamStatus(true);
        const response: SlamStatusResponse = await getSlamStatus({
          organization_id,
        });
        setSlamEnabled(response.slam_enabled);
        setInitialSlamEnabled(response.slam_enabled);
      } catch (err) {
        console.error('Failed to fetch SLAM status:', err);
      } finally {
        setIsLoadingSlamStatus(false);
      }
    };

    fetchSlamStatus();
  }, [organization_id, getSlamStatus]);

  const handleConfigChange = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (allowedSpace <= 0) return;

    try {
      setIsUpdating(true);
      // Only update SLAM status if changed
      if (slamEnabled !== initialSlamEnabled) {
        await setSlamStatus({ organization_id, slam_enabled: slamEnabled });
      }
      if (allowedSpace !== allowed_space) {
        await updateOrgAllowedSpace({
          allowed_space: allowedSpace,
          organization_id,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['adminDashboardSummary'],
        refetchType: 'all',
      });

      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };
  return (
    <Box
      as="form"
      display="grid"
      rowGap={6}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleConfigChange}
      minW={480}
      data-testid="admin-storage-config-form"
    >
      <Checkbox
        isChecked={slamEnabled}
        onChange={(e) => setSlamEnabled(e.target.checked)}
        label="Enable SLAM (generate slam files for uploaded data)"
        isDisabled={isLoadingSlamStatus}
        mb={2}
      />

      <FormControl paddingInline={0}>
        <FormLabel textTransform="capitalize">Set Storage Limit</FormLabel>
        <InputGroup>
          <TextInput
            type="number"
            isRequired
            value={allowedSpace / 1_000_000_000} // Convert bytes to GB for display
            onChange={(e) =>
              setAllowedSpace(parseFloat(e.target.value) * 1_000_000_000)
            }
            autoFocus
            onFocus={(e) => e.target.select()}
            data-testid="admin-storage-config-input"
          />
          <InputRightAddon>GB</InputRightAddon>
        </InputGroup>
        <FormHelperText minH={8}>
          {allowedSpace !== allowed_space ? (
            <>
              The storage limit will be changed from{' '}
              <Box as="span" fontWeight={600}>
                {formatBytesToGB(allowed_space)}
              </Box>{' '}
              to{' '}
              <Box as="span" fontWeight={600}>
                {formatBytesToGB(allowedSpace)}
              </Box>
            </>
          ) : (
            ''
          )}
        </FormHelperText>
      </FormControl>
      <Button
        minWidth={150}
        justifySelf="end"
        type="submit"
        isDisabled={isUpdating}
        data-testid="admin-storage-config-save"
      >
        Save
      </Button>
    </Box>
  );
}
